import type { Project, Experience, SkillGroup, AiCard, ProcessStep } from "./types";

// ── PROJECTS ─────────────────────────────────────────────────────────────
// NOTE: This array mimics an API response. To connect a real backend later,
// replace `projects` with a fetch() call returning the same `Project[]` shape.
export const projects: Project[] = [
  {
    id: "crm-insurance",
    title: {
      es: "CRM Inteligente para Seguros",
      en: "Intelligent CRM for Insurance",
    },
    description: {
      es: "Plataforma para gestión comercial, sugerencias de productos, seguimiento de clientes y visualización de oportunidades.",
      en: "Platform for commercial management, product suggestions, client follow-up and opportunity visualization.",
    },
    stack: ["React", "TypeScript", "Tailwind", "Laravel", "SQL Server", "AI"],
    category: "fullstack",
    status: "production",
  },
  {
    id: "churn-prediction",
    title: {
      es: "Motor de Predicción de Cancelación",
      en: "Policy Churn Prediction Engine",
    },
    description: {
      es: "Módulo de machine learning para estimar la probabilidad de cancelación de pólizas y mostrar los factores principales.",
      en: "Machine learning module to estimate policy cancellation probability and surface the main driving factors.",
    },
    stack: ["Python", "FastAPI", "ML", "Pandas", "SQL Server", "React"],
    category: "ai",
    status: "prototype",
  },
  {
    id: "ai-product-assistant",
    title: {
      es: "Asistente IA para Productos",
      en: "AI Product Assistant",
    },
    description: {
      es: "Asistente capaz de analizar el contexto del cliente y sugerir productos usando modelos generativos y reglas de negocio.",
      en: "Assistant that analyzes client context and recommends products using generative models and business rules.",
    },
    stack: ["Gemini", "Ollama", "RAG", "Laravel", "Redis", "React"],
    category: "ai",
    status: "lab",
  },
  {
    id: "biometric-auth",
    title: {
      es: "Autenticación Biométrica Local",
      en: "Local Biometric Authentication",
    },
    description: {
      es: "Prototipo experimental de autenticación facial usando cámara local, detección facial y almacenamiento seguro.",
      en: "Experimental facial-authentication prototype using a local camera, face detection and secure storage.",
    },
    stack: ["Python", "Computer Vision", "React", "FastAPI"],
    category: "backend",
    status: "experimental",
  },
];

// ── EXPERIENCE ───────────────────────────────────────────────────────────
export const experiences: Experience[] = [
  {
    id: "senior-frontend",
    role: { es: "Senior Frontend Developer", en: "Senior Frontend Developer" },
    org: { es: "Sector Seguros / Fintech", en: "Insurance / Fintech Sector" },
    period: { es: "2022 — Presente", en: "2022 — Present" },
    bullets: [
      {
        es: "Desarrollo de interfaces con React, TypeScript y Tailwind CSS.",
        en: "Building interfaces with React, TypeScript and Tailwind CSS.",
      },
      {
        es: "Integración con APIs REST y servicios internos.",
        en: "Integration with REST APIs and internal services.",
      },
      {
        es: "Construcción de módulos CRM, cotización, dashboards y operación.",
        en: "Building CRM, quoting, dashboard and operations modules.",
      },
      {
        es: "Colaboración con backend Laravel, SQL Server y servicios de IA.",
        en: "Collaboration with Laravel backend, SQL Server and AI services.",
      },
    ],
  },
  {
    id: "fullstack-ai",
    role: { es: "Full-Stack / Proyectos con IA Aplicada", en: "Full-Stack / Applied AI Projects" },
    org: { es: "Proyectos e I+D", en: "Products & R&D" },
    period: { es: "2021 — Presente", en: "2021 — Present" },
    bullets: [
      { es: "Prototipos y servicios con FastAPI.", en: "Prototypes and services with FastAPI." },
      { es: "Modelos predictivos para negocio.", en: "Predictive models for business." },
      { es: "RAG y bases de datos vectoriales.", en: "RAG and vector databases." },
      { es: "Asistentes internos potenciados con IA.", en: "AI-powered internal assistants." },
    ],
  },
];

// ── SKILLS ───────────────────────────────────────────────────────────────
export const skillGroups: SkillGroup[] = [
  {
    id: "frontend",
    title: { es: "Frontend", en: "Frontend" },
    icon: "Layout",
    skills: ["React", "TypeScript", "JavaScript", "Tailwind CSS", "Next.js", "Formik", "UX/UI", "Responsive Design"],
  },
  {
    id: "backend",
    title: { es: "Backend", en: "Backend" },
    icon: "Server",
    skills: ["Laravel", "PHP", "FastAPI", "Python", "Node.js", "APIs REST"],
  },
  {
    id: "databases",
    title: { es: "Bases de datos", en: "Databases" },
    icon: "Database",
    skills: ["SQL Server", "PostgreSQL", "MySQL", "Redis"],
  },
  {
    id: "ai",
    title: { es: "IA aplicada", en: "Applied AI" },
    icon: "Sparkles",
    skills: ["RAG", "ChromaDB", "LlamaIndex", "Gemini", "Ollama", "Modelos predictivos", "Prompt design", "Integración de IA"],
  },
  {
    id: "devops",
    title: { es: "DevOps / Herramientas", en: "DevOps / Tooling" },
    icon: "Wrench",
    skills: ["Docker", "Git", "GitHub Actions", "Postman", "Redis", "Linux", "VPS"],
  },
];

// ── APPLIED AI ───────────────────────────────────────────────────────────
export const aiCards: AiCard[] = [
  {
    id: "recommendations",
    title: { es: "Recomendaciones inteligentes", en: "Smart recommendations" },
    description: {
      es: "Sugerencias de productos basadas en el contexto del cliente, historial y reglas de negocio.",
      en: "Product suggestions driven by client context, history and business rules.",
    },
    icon: "Wand2",
  },
  {
    id: "churn",
    title: { es: "Predicción de cancelación", en: "Churn prediction" },
    description: {
      es: "Modelos que estiman riesgo de cancelación y explican los factores que más influyen.",
      en: "Models that estimate cancellation risk and explain the most influential factors.",
    },
    icon: "TrendingDown",
  },
  {
    id: "rag",
    title: { es: "Asistentes internos / RAG", en: "Internal assistants / RAG" },
    description: {
      es: "Asistentes que consultan documentación y datos internos con recuperación aumentada por contexto.",
      en: "Assistants that query internal docs and data using retrieval-augmented generation.",
    },
    icon: "Bot",
  },
];

// ── PROCESS ──────────────────────────────────────────────────────────────
export const processSteps: ProcessStep[] = [
  {
    id: "understand",
    title: { es: "Entender el problema", en: "Understand the problem" },
    description: {
      es: "Analizo el objetivo de negocio y los usuarios antes de escribir código.",
      en: "I analyze the business goal and users before writing any code.",
    },
  },
  {
    id: "design",
    title: { es: "Diseñar la experiencia", en: "Design the experience" },
    description: {
      es: "Defino flujos, jerarquía visual y accesibilidad centrados en el usuario.",
      en: "I define flows, visual hierarchy and accessibility centered on the user.",
    },
  },
  {
    id: "build",
    title: { es: "Construir frontend escalable", en: "Build a scalable frontend" },
    description: {
      es: "Componentes reutilizables, tipados y mantenibles a largo plazo.",
      en: "Reusable, typed components that stay maintainable over time.",
    },
  },
  {
    id: "integrate",
    title: { es: "Integrar APIs y datos", en: "Integrate APIs & data" },
    description: {
      es: "Conecto servicios REST, bases de datos y fuentes de datos reales.",
      en: "I connect REST services, databases and real data sources.",
    },
  },
  {
    id: "optimize",
    title: { es: "Medir, optimizar y mejorar", en: "Measure, optimize & improve" },
    description: {
      es: "Reviso rendimiento y métricas para iterar sobre resultados reales.",
      en: "I review performance and metrics to iterate on real outcomes.",
    },
  },
  {
    id: "ai",
    title: { es: "Añadir IA cuando aporta valor", en: "Add AI when it adds value" },
    description: {
      es: "Integro IA solo cuando resuelve un problema real, no por moda.",
      en: "I bring in AI only when it solves a real problem, not for hype.",
    },
  },
];

// ── HERO BADGES ──────────────────────────────────────────────────────────
export const heroBadges = ["React", "TypeScript", "Tailwind", "Laravel", "FastAPI", "SQL Server", "AI/RAG"];

// ── CONTACT (dummy links only, no sensitive data) ─────────────────────────
export const contactLinks = {
  email: import.meta.env.VITE_EMAIL,
  linkedin: import.meta.env.VITE_LINKEDIN_URL,
  github: import.meta.env.VITE_GITHUB_URL,
  portfolio: "https://danielcamacho.dev",
};
